import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Type } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { from, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

import { CurrentSiteState } from '@ppg/configuration';
import { MAIN_CONTENT_ROUTE_DATA_KEY } from '@ppg/core/content';
import { ClientSiteSettingsKeys, PageType } from '@ppg/core/enums';
import { Content, Page, PageContent } from '@ppg/core/models';
import { ContentBannerThemeService, createPendingTaskWatcher } from '@ppg/core/services';
import { BreadcrumbsComponent } from '@ppg/shared/breadcrumbs';
@Component({
  selector: 'ppg-page-resolver',
  standalone: true,
  imports: [CommonModule, BreadcrumbsComponent],
  templateUrl: './page-resolver.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageResolverComponent {
  readonly #contentBannerThemeService = inject(ContentBannerThemeService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #currentSiteState = inject(CurrentSiteState);

  readonly #routeData = toSignal(this.#activatedRoute.data, { initialValue: this.#activatedRoute.snapshot.data });
  readonly #mainContent = computed<Content | null>(() => this.#routeData()[MAIN_CONTENT_ROUTE_DATA_KEY] ?? null);

  readonly pageWithModifications = computed<Page | null>(() => {
    const pageContent = this.#mainContent()?.page.content as PageContent;
    return {
      ...this.#mainContent()?.page,
      content: this.#contentBannerThemeService.processContentBannerTheme(pageContent),
    } as Page;
  });

  readonly componentInputs = computed(() => {
    return { page: this.page() };
  });

  readonly breadcrumbHomeCodename = this.#currentSiteState.getSiteSettingValueByKey(
    ClientSiteSettingsKeys.FeatureBreadcrumbsHome,
  );
  readonly breadcrumbsHome = computed(() => this.#mainContent()?.resources[this.breadcrumbHomeCodename]);
  readonly breadcrumbs = computed(() => {
    const isArticlePage = this.#mainContent()?.page?.type === PageType.PAGE_ARTICLE;
    return isArticlePage ? [] : (this.#mainContent()?.breadcrumbs ?? null);
  });

  readonly page = computed(() => {
    return this.pageWithModifications() ?? null;
  });

  readonly #pageRendererComponentLoadTask = createPendingTaskWatcher();
  readonly pageRendererComponent$ = of(null).pipe(
    tap(() => this.#pageRendererComponentLoadTask.startTask()),
    switchMap(() => this.#getPageRenderer()),
    tap(() => this.#pageRendererComponentLoadTask.stopTask()),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  #getPageRenderer(): Observable<Type<unknown>> {
    return from(import('@ppg/features/page-renderer').then((m) => m.PageRendererComponent));
  }
}
